import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  Menu,
  X,
  Info,
  Wifi,
  Briefcase,
  MapPin,
  UserRound,
  Receipt,
  MessageCircle,
  ChevronRight,
  Phone,
  Server,
  HardDrive,
  Camera,
  Smartphone,
  Newspaper,
  Mail,
} from "lucide-react";
import { useBrand, useSiteSection, waLink } from "@/hooks/useSiteContent";
import { siteImageUrl } from "@/hooks/useSiteData";
import { productMessage } from "@/lib/productLine";
import logoContato from "@/assets/logo-contato.svg";

const dataCenterUrl = "https://contatocloud.com.br/";

type NavItem = {
  label: string;
  icon: typeof Info;
  to?: string;
  href?: string;
};

const nav: NavItem[] = [
  { label: "Sobre Nós", to: "/sobre", icon: Info },
  { label: "Internet", to: "/planos", icon: Wifi },
  { label: "Telefonia", to: "/telefonia", icon: Phone },
  { label: "Hospedagem", to: "/hospedagem", icon: Server },
  { label: "Data Center", href: dataCenterUrl, icon: HardDrive },
  { label: "Câmeras", to: "/cameras", icon: Camera },
  { label: "Aplicativos", to: "/aplicativos", icon: Smartphone },
  { label: "Blog Contato", to: "/blog", icon: Newspaper },
  { label: "Trabalhe Conosco", to: "/trabalhe-conosco", icon: Briefcase },
  { label: "Lojas", to: "/lojas", icon: MapPin },
  { label: "Área do Cliente", to: "/area-do-cliente", icon: UserRound },
];

const topBar = [
  { label: "Sobre Nós", to: "/sobre" },
  { label: "Blog Contato", to: "/blog" },
  { label: "Trabalhe Conosco", to: "/trabalhe-conosco" },
  { label: "Lojas", to: "/lojas" },
];

const alwaysVisibleTabs: NavItem[] = [
  { label: "Internet", to: "/planos", icon: Wifi },
  { label: "Telefonia", to: "/telefonia", icon: Phone },
  { label: "Hospedagem", to: "/hospedagem", icon: Server },
  { label: "Data Center", href: dataCenterUrl, icon: HardDrive },
];

const extraTabs: NavItem[] = [
  { label: "Câmeras", to: "/cameras", icon: Camera },
  { label: "Aplicativos", to: "/aplicativos", icon: Smartphone },
];

function TopBarLink({ to, href, label }: { to?: string; href?: string; label: string }) {
  const cls =
    "text-[0.68rem] font-bold uppercase tracking-[0.05em] text-primary-foreground/90 transition-colors hover:text-brand-yellow";
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={cls}>
        {label}
      </a>
    );
  }
  return (
    <Link to={to!} className={cls}>
      {label}
    </Link>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const brand = useBrand();
  const integrations = useSiteSection("integrations");
  const clientPortalUrl = brand.clientPortalUrl;
  const whatsappLink = (m: string) => waLink(brand.whatsapp, m, "topo do site");
  const pathname = useRouterState({ select: (r) => r.location.pathname });
  const topMessage = productMessage(pathname);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <header className="sticky top-0 z-50 bg-background">
      {/* Faixa superior azul (desktop) */}
      <div className="hidden bg-primary text-primary-foreground lg:block">
        <div className="mx-auto flex h-12 max-w-7xl items-center justify-between gap-4 px-5">
          <div className="flex items-center justify-center gap-5">
            {topBar.map((item) => (
              <TopBarLink key={item.label} to={item.to} label={item.label} />
            ))}
            {integrations.webmailUrl ? (
              <TopBarLink href={integrations.webmailUrl} label="Webmail" />
            ) : null}
            <TopBarLink href={clientPortalUrl} label="Área do Cliente" />
          </div>

          <a
            href={whatsappLink(topMessage)}
            data-wa-loc="topo"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm font-extrabold tracking-wide"
          >
            <MessageCircle className="size-5" strokeWidth={2.4} />
            {brand.phoneDisplay}
          </a>
        </div>
      </div>

      <div className="mx-auto grid h-20 max-w-7xl grid-cols-[auto_minmax(0,1fr)] items-center gap-4 px-5 lg:flex lg:h-24 lg:justify-between lg:gap-8">
        <button
          type="button"
          aria-label={open ? "Fechar menu" : "Abrir menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex size-11 items-center justify-center rounded-xl text-primary transition-colors hover:bg-secondary lg:hidden"
        >
          {open ? <X className="size-7" /> : <Menu className="size-7" strokeWidth={2.5} />}
        </button>

        <Link to="/" className="justify-self-end lg:order-first" onClick={() => setOpen(false)}>
          <Logo className="h-9 lg:h-11" />
        </Link>

        {/* Serviços (desktop) */}
        <nav className="hidden min-w-0 flex-1 items-center justify-center gap-4 lg:flex xl:gap-6">
          {alwaysVisibleTabs.map((item) => {
            const active = item.to ? pathname === item.to : false;
            const href =
              item.label === "Data Center"
                ? integrations.dataCenterUrl || item.href
                : item.href;
            const cls = `group inline-flex items-center gap-2 whitespace-nowrap text-xs font-extrabold uppercase tracking-wide transition-colors ${
              active ? "text-primary" : "text-brand-deep hover:text-primary"
            }`;
            const content = (
              <>
                <item.icon
                  className="size-5 text-brand-sky transition-colors group-hover:text-primary"
                  strokeWidth={2.2}
                />
                {item.label}
              </>
            );
            if (href) {
              return (
                <a key={item.label} href={href} target="_blank" rel="noreferrer" className={cls}>
                  {content}
                </a>
              );
            }
            return (
              <Link key={item.label} to={item.to!} className={cls}>
                {content}
              </Link>
            );
          })}

          {extraTabs.map((item) => {
            const active = item.to ? pathname === item.to : false;
            const cls = `group hidden xl:inline-flex items-center gap-2 whitespace-nowrap text-xs font-extrabold uppercase tracking-wide transition-colors ${
              active ? "text-primary" : "text-brand-deep hover:text-primary"
            }`;
            const content = (
              <>
                <item.icon
                  className="size-5 text-brand-sky transition-colors group-hover:text-primary"
                  strokeWidth={2.2}
                />
                {item.label}
              </>
            );
            return (
              <Link key={item.label} to={item.to!} className={cls}>
                {content}
              </Link>
            );
          })}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={whatsappLink(topMessage)}
            data-wa-loc="topo"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-brand-lime px-4 py-2 text-xs font-extrabold uppercase tracking-wide text-brand-deep transition-transform hover:-translate-y-0.5"
          >
            <MessageCircle className="size-4" strokeWidth={2.6} />
            Assine já
          </a>
        </div>
      </div>

      {/* Overlay */}
      <button
        type="button"
        aria-label="Fechar menu"
        tabIndex={open ? 0 : -1}
        onClick={() => setOpen(false)}
        className={`fixed inset-0 top-20 z-40 bg-brand-deep/50 backdrop-blur-[2px] transition-opacity duration-300 lg:hidden ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />

      {/* Drawer */}
      <nav
        aria-hidden={!open}
        className={`fixed left-0 top-20 z-50 flex h-[calc(100dvh-5rem)] w-[20rem] lg:hidden max-w-[88vw] flex-col bg-gradient-to-b from-brand-deep to-brand-sky-deep text-brand-deep-foreground shadow-2xl transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex-1 overflow-y-auto px-4 py-5">
          <p className="px-2 pb-3 text-[0.68rem] font-extrabold uppercase tracking-[0.18em] text-brand-sky">
            Navegue
          </p>

          <ul className="space-y-1.5">
            {nav.map((item) => {
              const active = item.to ? pathname === item.to : false;
              const href =
                item.label === "Data Center"
                  ? integrations.dataCenterUrl || item.href
                  : item.href;
              const baseCls = `flex items-center gap-3 rounded-2xl px-3 py-3.5 text-[0.95rem] font-bold transition-colors ${
                active
                  ? "bg-brand-yellow text-brand-yellow-foreground"
                  : "text-brand-deep-foreground hover:bg-white/10"
              }`;
              const iconSpan = (
                <span
                  className={`grid size-9 shrink-0 place-items-center rounded-xl ${
                    active ? "bg-brand-yellow-foreground/10" : "bg-white/10"
                  }`}
                >
                  <item.icon className="size-[1.15rem]" strokeWidth={2.4} />
                </span>
              );
              const content = (
                <>
                  {iconSpan}
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  <ChevronRight className="size-4 shrink-0 opacity-60" />
                </>
              );

              if (href) {
                return (
                  <li key={item.label}>
                    <a
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => setOpen(false)}
                      tabIndex={open ? 0 : -1}
                      className={baseCls}
                    >
                      {content}
                    </a>
                  </li>
                );
              }

              return (
                <li key={item.label}>
                  <Link
                    to={item.to!}
                    onClick={() => setOpen(false)}
                    tabIndex={open ? 0 : -1}
                    className={baseCls}
                  >
                    {content}
                  </Link>
                </li>
              );
            })}
          </ul>

          <p className="px-2 pb-3 pt-6 text-[0.68rem] font-extrabold uppercase tracking-[0.18em] text-brand-sky">
            Autoatendimento
          </p>

          <a
            href={clientPortalUrl}
            target="_blank"
            rel="noreferrer"
            tabIndex={open ? 0 : -1}
            className="flex items-center gap-3 rounded-2xl px-3 py-3.5 text-[0.95rem] font-bold transition-colors hover:bg-white/10"
          >
            <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-white/10">
              <Receipt className="size-[1.15rem]" strokeWidth={2.4} />
            </span>
            <span className="min-w-0 flex-1 truncate">Segunda Via do Boleto</span>
            <ChevronRight className="size-4 shrink-0 opacity-60" />
          </a>

          {integrations.webmailUrl ? (
            <a
              href={integrations.webmailUrl}
              target="_blank"
              rel="noreferrer"
              tabIndex={open ? 0 : -1}
              className="flex items-center gap-3 rounded-2xl px-3 py-3.5 text-[0.95rem] font-bold transition-colors hover:bg-white/10"
            >
              <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-white/10">
                <Mail className="size-[1.15rem]" strokeWidth={2.4} />
              </span>
              <span className="min-w-0 flex-1 truncate">Webmail</span>
              <ChevronRight className="size-4 shrink-0 opacity-60" />
            </a>
          ) : null}
        </div>

        <div className="border-t border-white/15 px-4 py-4">
          <a
            href={whatsappLink(topMessage)}
            data-wa-loc="topo"
            target="_blank"
            rel="noreferrer"
            tabIndex={open ? 0 : -1}
            className="flex items-center justify-center gap-2 rounded-full bg-brand-lime px-5 py-3.5 text-xs font-extrabold uppercase tracking-wide text-brand-deep transition-transform hover:-translate-y-0.5"
          >
            <MessageCircle className="size-4" strokeWidth={2.6} />
            Falar no WhatsApp
          </a>
          <p className="pt-3 text-center text-[0.7rem] text-brand-sky">
            Conexão de verdade, suporte de gente de verdade.
          </p>
        </div>
      </nav>
    </header>
  );
}

export function Logo({ className = "h-9" }: { className?: string }) {
  const brand = useBrand();
  const src = siteImageUrl(brand.logoUrl) ?? logoContato;
  return (
    <img
      src={src}
      alt="Contato Internet — conexão de verdade"
      className={`w-auto ${className}`}
      loading="eager"
      decoding="async"
    />
  );
}
